/*
 * Copyright 2020 The Yorkie Authors. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  ExecutionResult,
  Operation,
} from '@yorkie-js/sdk/src/document/operation/operation';
import { TimeTicket } from '@yorkie-js/sdk/src/document/time/ticket';
import { CRDTElement } from '@yorkie-js/sdk/src/document/crdt/element';
import { CRDTRoot } from '@yorkie-js/sdk/src/document/crdt/root';
import {
  Primitive,
  PrimitiveType,
} from '@yorkie-js/sdk/src/document/crdt/primitive';
import { CRDTCounter } from '@yorkie-js/sdk/src/document/crdt/counter';
import { Code, YorkieError } from '@yorkie-js/sdk/src/util/error';

/**
 * `IncreaseOperation` represents an operation that increments a numeric value to Counter.
 * Among Primitives, numeric types Integer, Long are used as values.
 */
export class IncreaseOperation extends Operation {
  private value: CRDTElement;
  private actor: string;

  constructor(
    parentCreatedAt: TimeTicket,
    value: CRDTElement,
    executedAt?: TimeTicket,
    actor?: string,
  ) {
    super(parentCreatedAt, executedAt);
    this.value = value;
    this.actor = actor || '';
  }

  /**
   * `create` creates a new instance of IncreaseOperation.
   */
  public static create(
    parentCreatedAt: TimeTicket,
    value: CRDTElement,
    executedAt?: TimeTicket,
    actor?: string,
  ): IncreaseOperation {
    return new IncreaseOperation(parentCreatedAt, value, executedAt, actor);
  }

  /**
   * `getActor` returns the actor ID associated with this operation.
   */
  public getActor(): string {
    return this.actor;
  }

  /**
   * `execute` executes this operation on the given `CRDTRoot`.
   */
  public execute(root: CRDTRoot): ExecutionResult {
    const parentObject = root.findByCreatedAt(this.getParentCreatedAt());
    if (!parentObject) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        `fail to find ${this.getParentCreatedAt()}`,
      );
    }
    if (!(parentObject instanceof CRDTCounter)) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        `fail to execute, only Counter can execute increase`,
      );
    }
    const counter = parentObject as CRDTCounter;
    const value = this.value.deepcopy() as Primitive;
    if (counter.isDedup()) {
      if (!this.actor) {
        throw new YorkieError(
          Code.ErrInvalidArgument,
          'dedup counter requires actor',
        );
      }
      counter.increaseDedup(value, this.actor);
    } else {
      counter.increase(value);
    }
    return {
      opInfos: [
        {
          type: 'increase',
          path: root.createPath(this.getParentCreatedAt()),
          value: value.getValue() as number,
        },
      ],
      // Dedup counters do not support undo/redo because HLL cannot
      // remove an actor once added.
      reverseOp: this.actor ? undefined : this.toReverseOperation(),
    };
  }

  /**
   * `toReverseOperation` returns the reverse operation of this operation.
   */
  private toReverseOperation(): Operation {
    const primitiveValue = this.value.deepcopy() as Primitive;

    const valueType = primitiveValue.getType();
    const value =
      valueType === PrimitiveType.Long
        ? -(primitiveValue.getValue() as bigint)
        : (primitiveValue.getValue() as number) * -1;

    const reverseOp = IncreaseOperation.create(
      this.getParentCreatedAt(),
      Primitive.of(value, primitiveValue.getCreatedAt()),
    );
    return reverseOp;
  }

  /**
   * `getEffectedCreatedAt` returns the creation time of the effected element.
   */
  public getEffectedCreatedAt(): TimeTicket {
    return this.getParentCreatedAt();
  }

  /**
   * `toTestString` returns a string containing the meta data.
   */
  public toTestString(): string {
    return `${this.getParentCreatedAt().toTestString()}.INCREASE.${this.value.toJSON()}`;
  }

  /**
   * `getValue` returns the value of this operation.
   */
  public getValue(): CRDTElement {
    return this.value;
  }
}
